<template>
  <main-layout
    bg-color="bg-primary-600"
  >
    <template #nav>
      <div class="flex flex-wrap items-center justify-between mx-auto">
        <router-link
          class="relative inline-flex items-center p-1.5 text-sm font-medium text-center rounded-full border-2 border-secondary-600 focus:ring-0 focus:outline-none focus:ring-primary-600"
          :to="{
            name: 'Home',
            query: {
              token: Jwt,
            },
          }">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4202">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        </router-link>
        <h1 class="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">Order Detail</h1>
      </div>
    </template>
    <div class="p-4">
      <div v-if="isLoading">
        loading
      </div>
      <slot v-if="details && !isLoading">
        <div class="p-4 rounded-md border-2 border-secondary-600 mb-4">
          <p class="text-lg font-bold">Order #{{ details.number }}</p>
          <p>
            <span class="font-semibold">Delivery method: </span>
            <span class="font-light">{{ details.deliveryMethodDetail.label }}</span>
          </p>
          <p v-if="details?.paymentMethod?.description">
            <span class="font-semibold">Payment method: </span>
            <span class="font-light">{{ details.paymentMethodMethodDetail.label }}</span>
          </p>
          <div>
            <span class="font-semibold">Status: </span>
            <span
              class="text-xs font-medium me-2 px-2.5 py-0.5 rounded text-white"
              :style="`background-color:${details.statusDetail.color}`"
            >
              {{ details.statusDetail.label }}
            </span>
          </div>
          <div>
            <span class="font-semibold">Created date: </span>
            <span class="font-light">{{ createdDate }}</span>
          </div>
        </div>
        <div class="p-4 rounded-md border-2 border-secondary-600 mb-4">
          <div class="flex justify-between">
            <div>
              <span class="text-lg font-bold">Items:</span>
              <span class="font-light"> ({{ totalItems }})</span>
            </div>
            <p class="text-lg font-bold">{{ currency }}{{ totalCost }}</p>
          </div>
          <order-item
            class="my-4"
            :key="item.id"
            :image-url="item.product.imageUrl"
            :name="item.product.name"
            :quantity="item.quantity"
            :currency="currency"
            :price="+item.product.price"
            v-for="item in items"
          />
        </div>
        <slot v-if="canActionOrder">
          <button
            @click="confirm"
            type="button"
            :disabled="isCancelling || isConfirming"
            class="w-full text-secondary-600 bg-primary-600 hover:bg-primary-500 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 focus:outline-none disabled:bg-disabled-300 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed disabled:bg-primary-500"
            v-if="allowedTransitions.includes(OrderStatus.CONFIRMED)"
          >
            <span v-if="isConfirming">
              <svg
                aria-hidden="true"
                role="status"
                class="inline w-4 h-4 mr-3 text-white animate-spin"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB" />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor" />
              </svg>
            </span>
            Confirm
          </button>
          <button
            v-if="allowedTransitions.includes(OrderStatus.CANCELLED)"
            @click="cancel"
            :disabled="isCancelling || isConfirming"
            type="button"
            class="w-full text-white bg-red-600 hover:bg-red-500 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 focus:outline-none disabled:bg-disabled-300 disabled:shadow-none disabled:ring-0 disabled:cursor-not-allowed disabled:bg-red-500"
          >
            <span v-if="isCancelling">
              <svg
                aria-hidden="true"
                role="status"
                class="inline w-4 h-4 mr-3 text-white animate-spin"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB" />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor" />
              </svg>
            </span>
            Cancel
          </button>
        </slot>
        <a
          v-if="details.invoiceId"
          :href="invoiceLink"
          target="_blank"
          rel="noopener noreferrer"
          class="w-full flex justify-center mb-2 items-center px-3 py-2.5 text-sm font-medium text-center text-white bg-gray-900 rounded-lg"
        >
          <span>View Invoice</span>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 ml-2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
          </svg>

        </a>
        <back-to-whats-app class="w-full flex justify-center" />
      </slot>
    </div>
  </main-layout>
</template>

<script setup>
import {
  Get,
  UpdateStatus,
} from '@/api/order';
import { initAccordions } from 'flowbite';
import { useRoute, useRouter } from 'vue-router';
import { format } from 'date-fns';

import { notify } from 'notiwind';

import {
  computed, onMounted, ref,
} from 'vue';
import { useAccountStore } from '@/stores/account';

import BackToWhatsApp from '@/components/shared/BackToWhatsApp.vue';
import OrderItem from '@/components/order/OrderItem.vue';
import MainLayout from '@/layouts/MainLayout.vue';
import { OrderStatus } from '@/constants';

const currency = computed(() => useAccountStore().Currency);

const route = useRoute();
const router = useRouter();
const details = ref(null);
const isCancelling = ref(false);
const isConfirming = ref(false);
const isLoading = ref(false);

const Jwt = computed(() => useAccountStore().Jwt);

const createdDate = computed(() => {
  if (!details.value.createdDate) return null;
  return format(new Date(details.value.createdDate), 'd MMM yyyy, HH:mm:ss');
});
const canActionOrder = computed(() => ![
  OrderStatus.CANCELLED, OrderStatus.DELIVERED, OrderStatus.COLLECTED,
].includes(details.value.status.id));
const allowedTransitions = computed(() => details.value?.allowedTransitions || []);
const invoiceLink = computed(() => {
  const link = router.resolve({
    name: 'Invoice',
    params: {
      id: details.value.invoiceId,
    },
  });
  return link.href;
});
const items = computed(() => (details.value?.items ?? []));
const totalItems = computed(() => (items.value ?? [])
  .reduce((accumulator, curValue) => accumulator + curValue.quantity, 0));
const totalCost = computed(() => (items.value ?? [])
// eslint-disable-next-line vue/max-len
  .reduce((accumulator, curValue) => accumulator + curValue.quantity * curValue.product.price, 0));

const load = async () => {
  try {
    isLoading.value = true;
    const { data: { data } } = await Get(route.params?.id ?? 'id-is-null');
    details.value = data;
  } catch (error) {
    let message;
    if (error.response) {
      message = error.response.data.message;
    }
    message = message ?? 'An error occured while loading the order';
    notify(
      {
        variant: 'bg-red-700',
        group: 'foo',
        text: message,
      },
      5000,
    );
  } finally {
    isLoading.value = false;
  }
};

const cancel = async () => {
  try {
    isCancelling.value = true;
    const { data: { message } } = await UpdateStatus({
      id: route.params?.id,
      status: OrderStatus.CANCELLED,
    });
    await load();
    notify(
      {
        variant: 'bg-green-700',
        group: 'foo',
        text: message,
      },
      5000,
    );
  } catch (error) {
    let message;
    if (error.response) {
      message = error.response.data.message;
    }
    message = message ?? 'An error occured while cancelling the order';
    notify(
      {
        variant: 'bg-red-700',
        group: 'foo',
        text: message,
      },
      5000,
    );
  } finally {
    isCancelling.value = false;
  }
};

const confirm = async () => {
  try {
    isConfirming.value = true;
    const { data: { message } } = await UpdateStatus({
      id: route.params?.id,
      status: OrderStatus.CONFIRMED,
    });
    await load();
    notify(
      {
        variant: 'bg-green-700',
        group: 'foo',
        text: message,
      },
      5000,
    );
  } catch (error) {
    let message;
    if (error.response) {
      message = error.response.data.message;
    }
    message = message ?? 'An error occured while confirming the order';
    notify(
      {
        variant: 'bg-red-700',
        group: 'foo',
        text: message,
      },
      5000,
    );
  } finally {
    isConfirming.value = false;
  }
};

onMounted(() => {
  initAccordions();
  load();
});

</script>
